import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminFetch, ApiClientError } from "../lib/apiClient";
import { AdminNav } from "../components/admin/AdminNav";
import type { AdminWebinar, ImportSummary, HubspotList } from "../types";

export function AdminWebinarsPage() {
  const [webinars, setWebinars] = useState<AdminWebinar[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  async function load() {
    try {
      const d = await adminFetch<{ webinars: AdminWebinar[] }>("/api/admin/webinars");
      setWebinars(d.webinars);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : "読み込みに失敗しました");
    }
  }

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="admin-page">
      <AdminNav current="webinars" />
      <header className="admin-header">
        <h1>ウェビナー一覧</h1>
        <button type="button" className="btn-secondary" onClick={() => setShowForm((s) => !s)}>
          {showForm ? "閉じる" : "新規作成"}
        </button>
      </header>

      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}

      {showForm && (
        <CreateWebinarForm
          onCreated={() => {
            setShowForm(false);
            load();
          }}
        />
      )}

      <table className="webinar-table">
        <thead>
          <tr>
            <th>タイトル</th>
            <th>slug</th>
            <th>状態</th>
            <th>URL</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {webinars.map((w) => (
            <tr key={w.id}>
              <td>{w.title}</td>
              <td>{w.slug}</td>
              <td>
                <span className={`status-badge status-${w.status}`}>{w.status}</span>
              </td>
              <td className="url-cell">
                <CopyUrl
                  label="視聴"
                  path={`/webinars/${w.slug}/watch?rid=...`}
                  copyText={`${window.location.origin}/webinars/${w.slug}/watch`}
                />
                <CopyUrl
                  label="OBS"
                  path={`/webinars/${w.slug}/display?token=${w.display_token}`}
                  copyText={`${window.location.origin}/webinars/${w.slug}/display?token=${w.display_token}`}
                />
              </td>
              <td className="webinar-actions">
                <Link className="btn-link" to={`/admin/webinars/${w.id}/questions`}>
                  質問管理
                </Link>
                <Link className="btn-link" to={`/admin/webinars/${w.id}/leads`}>
                  リード管理
                </Link>
                <EditPanel webinar={w} onUpdated={load} />
                <ImportPanel webinarId={w.id} />
              </td>
            </tr>
          ))}
          {webinars.length === 0 && (
            <tr>
              <td colSpan={5} className="empty">
                ウェビナーがありません
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// 視聴URL / OBS URL のコピーボタン。クリックで絶対URLをクリップボードへ。
function CopyUrl({
  label,
  path,
  copyText,
}: {
  label: string;
  path: string;
  copyText: string;
}) {
  const [copied, setCopied] = useState(false);

  async function onCopy() {
    try {
      await navigator.clipboard.writeText(copyText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // クリップボードが使えない環境では何もしない
    }
  }

  return (
    <button
      type="button"
      className={copied ? "url-copy url-copy--done" : "url-copy"}
      onClick={onCopy}
      title={copyText}
    >
      {label} <code>{path}</code>
      <span className="url-copy-icon">{copied ? "✓" : "📋"}</span>
    </button>
  );
}

function ImportPanel({ webinarId }: { webinarId: string }) {
  const [open, setOpen] = useState(false);
  const [lists, setLists] = useState<HubspotList[]>([]);
  const [listsLoading, setListsLoading] = useState(false);
  const [listsError, setListsError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [selectedListId, setSelectedListId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ImportSummary | null>(null);

  async function loadLists() {
    setListsLoading(true);
    setListsError(null);
    try {
      const d = await adminFetch<{ lists: HubspotList[] }>("/api/admin/lists");
      setLists(d.lists);
    } catch (err) {
      setListsError(
        err instanceof ApiClientError ? err.message : "リスト取得に失敗しました",
      );
    } finally {
      setListsLoading(false);
    }
  }

  function toggleOpen() {
    setOpen((o) => !o);
  }

  // open になったときだけリストを取得する。setOpen updater 内で副作用を持つと
  // StrictMode で二重実行され /api/admin/lists が二重リクエストになるため、
  // 状態更新とデータ取得を分離する。
  useEffect(() => {
    if (!open) return;
    setResult(null);
    setError(null);
    setSelectedListId("");
    setSearch("");
    loadLists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  async function runImport() {
    if (!selectedListId) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const summary = await adminFetch<ImportSummary>(
        `/api/admin/webinars/${webinarId}/import-registrations`,
        { method: "POST", body: JSON.stringify({ list_id: selectedListId }) },
      );
      setResult(summary);
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : "取り込みに失敗しました");
    } finally {
      setBusy(false);
    }
  }

  const q = search.trim().toLowerCase();
  const filtered = q
    ? lists.filter(
        (l) => l.name.toLowerCase().includes(q) || l.listId.includes(q),
      )
    : lists;

  return (
    <div className="import-panel">
      <button type="button" className="btn-secondary import-toggle" onClick={toggleOpen}>
        {open ? "閉じる" : "参加者取り込み"}
      </button>
      {open && (
        <div className="import-form">
          <input
            type="search"
            className="import-input"
            placeholder="リストを検索"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {listsLoading && <p className="muted">リスト取得中...</p>}
          {listsError && (
            <p className="form-error" role="alert">
              {listsError}
            </p>
          )}
          {!listsLoading && !listsError && (
            <ul className="import-list">
              {filtered.length === 0 ? (
                <li className="muted import-list-empty">該当するリストがありません</li>
              ) : (
                filtered.map((l) => (
                  <li key={l.listId}>
                    <button
                      type="button"
                      className={
                        selectedListId === l.listId
                          ? "import-list-item import-list-item-selected"
                          : "import-list-item"
                      }
                      onClick={() => setSelectedListId(l.listId)}
                    >
                      <span className="import-list-name">{l.name}</span>
                      <span className="import-list-size">{l.size}件</span>
                    </button>
                  </li>
                ))
              )}
            </ul>
          )}
          {error && (
            <p className="form-error" role="alert">
              {error}
            </p>
          )}
          <button
            type="button"
            className="btn-primary"
            disabled={busy || !selectedListId}
            onClick={runImport}
          >
            {busy ? "取り込み中..." : "取り込み実行"}
          </button>
          {result && (
            <div className="import-result">
              <p className="import-counts">
                新規: {result.imported} / 更新: {result.updated} / スキップ: {result.skipped}
                {" | "}視聴URL: {result.watch_url_set} / Note: {result.notes_created}
              </p>
              {!result.property_ready && (
                <p className="form-error">
                  HubSpotで <code>webinar_watch_url</code>（single-line text）プロパティを作成してください
                </p>
              )}
              {result.event === "unentitled" && (
                <p className="import-notice">
                  Marketing Event非対応のためNoteのみ記録されました
                </p>
              )}
              {result.errors.length > 0 && (
                <details className="import-errors">
                  <summary>エラー {result.errors.length} 件</summary>
                  <ul>
                    {result.errors.map((e, i) => (
                      <li key={i} className="import-error-item">
                        <span className="import-error-email">{e.email}</span>
                        <span className="import-error-stage">[{e.stage}]</span>
                        <span>{e.message}</span>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CreateWebinarForm({ onCreated }: { onCreated: () => void }) {
  const [form, setForm] = useState({
    title: "",
    slug: "",
    youtube_video_id: "",
    starts_at: "",
    description: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function update(key: keyof typeof form, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await adminFetch("/api/admin/webinars", {
        method: "POST",
        body: JSON.stringify({
          ...form,
          // datetime-local has no timezone; treat as ISO directly.
          starts_at: new Date(form.starts_at).toISOString(),
        }),
      });
      onCreated();
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : "作成に失敗しました");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="create-webinar-form" onSubmit={submit}>
      <label>
        タイトル
        <input value={form.title} onChange={(e) => update("title", e.target.value)} required />
      </label>
      <label>
        slug（英小文字・数字・ハイフン）
        <input value={form.slug} onChange={(e) => update("slug", e.target.value)} required />
      </label>
      <label>
        YouTube動画ID
        <input
          value={form.youtube_video_id}
          onChange={(e) => update("youtube_video_id", e.target.value)}
          required
        />
      </label>
      <label>
        開始日時
        <input
          type="datetime-local"
          value={form.starts_at}
          onChange={(e) => update("starts_at", e.target.value)}
          required
        />
      </label>
      <label>
        説明
        <input value={form.description} onChange={(e) => update("description", e.target.value)} />
      </label>
      {error && <p className="form-error">{error}</p>}
      <button type="submit" className="btn-primary" disabled={busy}>
        {busy ? "作成中..." : "作成"}
      </button>
    </form>
  );
}

// ISO文字列を datetime-local input 用のローカル時刻文字列に変換
function toDatetimeLocal(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

function EditPanel({
  webinar,
  onUpdated,
}: {
  webinar: AdminWebinar;
  onUpdated: () => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="edit-panel">
      <button
        type="button"
        className="btn-secondary"
        onClick={() => setOpen((o) => !o)}
      >
        {open ? "閉じる" : "編集"}
      </button>
      {open && (
        <EditWebinarForm
          webinar={webinar}
          onSaved={() => {
            setOpen(false);
            onUpdated();
          }}
        />
      )}
    </div>
  );
}

function EditWebinarForm({
  webinar,
  onSaved,
}: {
  webinar: AdminWebinar;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    title: webinar.title,
    youtube_video_id: webinar.youtube_video_id,
    starts_at: toDatetimeLocal(webinar.starts_at),
    description: webinar.description ?? "",
    status: webinar.status,
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function update(key: keyof typeof form, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await adminFetch(`/api/admin/webinars/${webinar.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          title: form.title,
          youtube_video_id: form.youtube_video_id,
          starts_at: new Date(form.starts_at).toISOString(),
          description: form.description || null,
          status: form.status,
        }),
      });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : "更新に失敗しました");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="create-webinar-form" onSubmit={submit}>
      <label>
        タイトル
        <input value={form.title} onChange={(e) => update("title", e.target.value)} required />
      </label>
      <label>
        YouTube動画ID
        <input
          value={form.youtube_video_id}
          onChange={(e) => update("youtube_video_id", e.target.value)}
          required
        />
      </label>
      <label>
        開始日時
        <input
          type="datetime-local"
          value={form.starts_at}
          onChange={(e) => update("starts_at", e.target.value)}
          required
        />
      </label>
      <label>
        説明
        <input value={form.description} onChange={(e) => update("description", e.target.value)} />
      </label>
      <label>
        状態
        <select value={form.status} onChange={(e) => update("status", e.target.value)}>
          <option value="draft">draft（下書き）</option>
          <option value="published">published（公開）</option>
          <option value="archived">archived（アーカイブ）</option>
        </select>
      </label>
      {error && <p className="form-error">{error}</p>}
      <button type="submit" className="btn-primary" disabled={busy}>
        {busy ? "保存中..." : "保存"}
      </button>
    </form>
  );
}
